/**
 * Copy-to-clipboard button — generic, shared by every theme.
 *
 * Markup contract: a button carrying `data-copy="<css selector>"`. On click, the referenced element's
 * value (an input's `value`, otherwise its text) goes to the clipboard, and the button wears `is-copied`
 * for a moment so the theme can acknowledge it. Nothing else is assumed: no wrapper, no id convention,
 * no class on the source.
 *
 * Add `data-copy-html` and the markup travels too, alongside the text — for a source whose SHAPE is
 * part of the answer, a table of form answers being the case it was written for. Pasted into a word
 * processor or a mail it stays a table; pasted into a text field it is still readable.
 *
 * Delegated on `document` rather than bound per button, because the elements it serves are RE-RENDERED —
 * a tool answers its own page with the result in it, and an ajax form replaces its target in place. A
 * listener attached at load would have gone with the old node.
 *
 * Writing to the clipboard can be refused, and in more cases than "old browser": the API is absent
 * outside a secure origin, and `writeText` rejects when the document does not hold focus or when the
 * permission is denied. A refusal must not be silence — the visitor clicked, and would be left
 * believing they hold a password they do not. So on failure the value is SELECTED instead, which puts
 * the usual keyboard shortcut one keystroke away and shows, visibly, what would have been copied.
 */
function copiedValue(source) {
	if (source.value !== undefined) {
		return source.value;
	}
	// A table's textContent runs every cell together with nothing between them, which is unreadable
	// and unpasteable. Rebuilt row by row with a TAB between cells: that is the separator a
	// spreadsheet splits columns on, so the same clipboard serves a mail and a spreadsheet.
	const rows = source.querySelectorAll('tr');
	if (rows.length) {
		return Array.from(rows)
				.map((row) => Array.from(row.cells).map((cell) => cell.textContent.trim()).join('\t'))
				.join('\n');
	}
	return source.textContent;
}

/**
 * Rich copy, for a button that asks for it with `data-copy-html`: the clipboard carries the markup
 * AND a plain-text fallback, so pasting into a word processor or a mail keeps the table while
 * pasting into a text field still gets something readable. Two flavours of one copy, not two copies.
 *
 * `ClipboardItem` is younger than `writeText` and absent from some browsers we still serve, and it
 * is refused outside a secure origin like the rest of the API. Returning null says "cannot", and the
 * caller falls back to the plain path rather than losing the copy altogether.
 */
function richCopy(source) {
	if (typeof ClipboardItem === 'undefined' || !navigator.clipboard.write) {
		return null;
	}
	return navigator.clipboard.write([new ClipboardItem({
		'text/html': new Blob([source.innerHTML], { type: 'text/html' }),
		'text/plain': new Blob([copiedValue(source)], { type: 'text/plain' }),
	})]);
}

function flag(button, state) {
	button.classList.add(state);
	setTimeout(() => button.classList.remove(state), 1500);
}

/** Last resort: show the value as selected, so the visitor finishes the job with their own shortcut. */
function selectInstead(button, source) {
	const range = document.createRange();
	range.selectNodeContents(source);
	const selection = window.getSelection();
	selection.removeAllRanges();
	selection.addRange(range);
	flag(button, 'is-copy-failed');
}

document.addEventListener('click', (event) => {
	const button = event.target.closest('[data-copy]');
	if (!button) {
		return;
	}

	const source = document.querySelector(button.getAttribute('data-copy'));
	if (!source) {
		return;
	}

	event.preventDefault();
	if (!navigator.clipboard) {
		selectInstead(button, source);
		return;
	}

	const rich = button.hasAttribute('data-copy-html') ? richCopy(source) : null;
	(rich || navigator.clipboard.writeText(copiedValue(source)))
			.then(() => flag(button, 'is-copied'))
			.catch(() => selectInstead(button, source));
});
